// System
import { Select } from '@mui/material';
import { useMemo } from 'react';
import { MenuItemEx } from '@Components/shared/selects/MenuItemEx';
import { useGeneral } from '@Modal/hooks/general/useGeneral';
import { useAuthorization } from '@Modal/hooks/authorization/useAuthorization';
import { useLocation } from 'react-router';
import { ProvidersTableGames, ProvidersTableGamesKeys } from '@Modal/reducers/providers/types';

// Either permission is enough to put a game in the dropdown: the round tables
// gate on CAN_VIEW_*_ROUNDS, so a view-only user has to be able to switch to it.
const gamePermissions: Record<ProvidersTableGamesKeys, string[]> = {
  1: ['CAN_VIEW_AVIATOR_ROUNDS', 'CAN_MANAGE_AVIATOR_ROUNDS'],
  2: ['CAN_VIEW_MINES_ROUNDS', 'CAN_MANAGE_MINES_ROUNDS'],
  3: ['CAN_VIEW_RUBBER_DUCK_ROUNDS', 'CAN_MANAGE_RUBBER_DUCK_ROUNDS'],
};

const allGames = Object.keys(ProvidersTableGames).map(Number) as ProvidersTableGamesKeys[];

const GamesDropdown = () => {
  const { handleGameChange, game } = useGeneral();
  const { currentUser, currentProvider, userPermissions } = useAuthorization();
  const location = useLocation();

  const allowedPaths = ['/providers', '/transactions', '/languages', '/rounds'];
  const isAllowedPage = allowedPaths.some((path) => location.pathname.endsWith(path));

  const isSuper = currentUser?.isSuper;
  const userGames = currentProvider?.games;

  const dropdownConfig = useMemo(() => {
    const options = allGames.filter(
      (gameId) =>
        isSuper ||
        userGames?.includes(gameId) ||
        gamePermissions[gameId].some((permission) => userPermissions?.includes(permission))
    );

    return {
      options,
      shouldShowSelect: options.length > 1,
      currentGame: game,
    };
  }, [game, isSuper, userGames, userPermissions]);

  if (!isAllowedPage || !dropdownConfig.shouldShowSelect) return null;

  return (
    <Select
      sx={{ height: '32px' }}
      value={dropdownConfig.currentGame}
      onChange={(event) => handleGameChange(event.target.value as number)}
    >
      {dropdownConfig.options.map((gameId) => (
        <MenuItemEx key={gameId} name={'Game'} value={gameId}>
          {ProvidersTableGames[gameId]}
        </MenuItemEx>
      ))}
    </Select>
  );
};

export default GamesDropdown;
